import React, { useState, useEffect } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { getAllUsers, createUser, updateUserRole, toggleUserActive, deleteUser, updateUserProfile, ROLE_LABELS, ROLE_COLORS } from '../../services/rbacService';

import { Users, Plus, Search, Shield, Edit2, Trash2, UserCheck, UserX, X, Eye, EyeOff, AlertTriangle, CheckCircle, RefreshCw, Crown } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'agent', label: 'Agent' },
  { value: 'chauffeur', label: 'Chauffeur' },
  { value: 'agent_abidjan', label: 'Agent Abidjan' },
];

export default function AdminUsersPermissions() {
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [toast, setToast] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadUsers = async () => {
    setLoading(true);
    const { data, error } = await getAllUsers();
    if (!error && data) {
      setUsers(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const filteredUsers = users?.filter((u) => {
    const matchSearch = !searchQuery || 
      u?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      u?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchRole = filterRole === 'all' || u?.role === filterRole;
    return matchSearch && matchRole;
  });

  const handleToggleActive = async (targetUser) => {
    const newStatus = !targetUser?.is_active;
    const { error } = await toggleUserActive(targetUser?.id, newStatus, currentUser?.id);
    if (!error) {
      showToast(`Utilisateur ${newStatus ? 'activé' : 'désactivé'} avec succès.`);
      loadUsers();
    } else {
      showToast('Erreur lors de la modification du statut.', 'error');
    }
  };

  const handleRoleChange = async (targetUser, newRole) => {
    if (targetUser?.role === 'super_admin') {
      showToast('Impossible de modifier le rôle du Super Admin.', 'error');
      return;
    }
    const { error } = await updateUserRole(targetUser?.id, newRole, currentUser?.id);
    if (!error) {
      showToast('Rôle mis à jour avec succès.');
      loadUsers();
    } else {
      showToast('Erreur lors du changement de rôle.', 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    const { error } = await deleteUser(selectedUser?.id, currentUser?.id);
    if (!error) {
      showToast('Utilisateur supprimé avec succès.');
      setShowDeleteModal(false);
      setSelectedUser(null);
      loadUsers();
    } else {
      showToast('Erreur lors de la suppression.', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${
          toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          {toast?.type === 'error' ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
          {toast?.message}
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <span>Administration</span>
              <span>›</span>
              <span className="text-gray-900 dark:text-white font-medium">Utilisateurs & Permissions</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Shield className="h-6 w-6 text-purple-600" />
              Gestion des Utilisateurs
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {users?.length} utilisateur{users?.length !== 1 ? 's' : ''} dans le système
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadUsers}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nouvel utilisateur
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
          >
            <option value="all">Tous les rôles</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="agent">Agent</option>
            <option value="chauffeur">Chauffeur</option>
            <option value="agent_abidjan">Agent Abidjan</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : filteredUsers?.length === 0 ? (
            <div className="text-center py-16">
              <Users className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Utilisateur</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rôle</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Statut</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Créé le</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dernière connexion</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {filteredUsers?.map((u) => (
                    <UserRow
                      key={u?.id}
                      user={u}
                      currentUser={currentUser}
                      onToggleActive={handleToggleActive}
                      onRoleChange={handleRoleChange}
                      onEdit={() => { setSelectedUser(u); setShowEditModal(true); }}
                      onDelete={() => { setSelectedUser(u); setShowDeleteModal(true); }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          currentUser={currentUser}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); loadUsers(); showToast('Utilisateur créé avec succès.'); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          currentUser={currentUser}
          onClose={() => { setShowEditModal(false); setSelectedUser(null); }}
          onSuccess={() => { setShowEditModal(false); setSelectedUser(null); loadUsers(); showToast('Utilisateur mis à jour.'); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}
      {showDeleteModal && selectedUser && (
        <DeleteConfirmModal
          user={selectedUser}
          onClose={() => { setShowDeleteModal(false); setSelectedUser(null); }}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

// ─── UserRow ──────────────────────────────────────────────────
function UserRow({ user, currentUser, onToggleActive, onRoleChange, onEdit, onDelete }) {
  const isSelf = user?.id === currentUser?.id;
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {user?.full_name ? user?.full_name?.charAt(0)?.toUpperCase() : user?.email?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1">
              {user?.full_name || '—'}
              {isSelf && <span className="text-xs text-green-600 dark:text-green-400">(vous)</span>}
              {isSuperAdmin && <Crown className="h-3.5 w-3.5 text-purple-500" />}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        {isSuperAdmin ? (
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${ROLE_COLORS?.[user?.role]}`}>
            <Crown className="h-3 w-3" />
            {ROLE_LABELS?.[user?.role]}
          </span>
        ) : (
          <select
            value={user?.role || 'admin'}
            onChange={(e) => onRoleChange(user, e?.target?.value)}
            className="text-xs px-2 py-1 border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-green-500 outline-none"
          >
            {ROLE_OPTIONS?.map((r) => (
              <option key={r?.value} value={r?.value}>{r?.label}</option>
            ))}
          </select>
        )}
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
          user?.is_active !== false ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {user?.is_active !== false ? <UserCheck className="h-3 w-3" /> : <UserX className="h-3 w-3" />}
          {user?.is_active !== false ? 'Actif' : 'Désactivé'}
        </span>
      </td>
      <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">
        {user?.created_at ? new Date(user?.created_at)?.toLocaleDateString('fr-FR') : '—'}
      </td>
      <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">
        {user?.last_login_at ? new Date(user?.last_login_at)?.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : 'Jamais'}
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-1">
          {!isSuperAdmin && (
            <>
              <button
                onClick={() => onEdit(user)}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Modifier"
              >
                <Edit2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => onToggleActive(user)}
                className={`p-1.5 rounded-lg transition-colors ${
                  user?.is_active !== false
                    ? 'text-gray-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20' :'text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}
                title={user?.is_active !== false ? 'Désactiver' : 'Activer'}
              >
                {user?.is_active !== false ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
              </button>
              {!isSelf && (
                <button
                  onClick={() => onDelete(user)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── CreateUserModal ──────────────────────────────────────────
function CreateUserModal({ currentUser, onClose, onSuccess, onError }) {
  const [form, setForm] = useState({ email: '', fullName: '', role: 'admin', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.email || !form?.password || !form?.fullName) return;
    setLoading(true);

    const { data, error } = await createUser({
      email: form?.email,
      password: form?.password,
      fullName: form?.fullName,
      role: form?.role,
      createdBy: currentUser?.id,
    });

    setLoading(false);
    if (error) {
      const msg = error?.message || '';
      if (msg?.includes('already registered') || msg?.includes('already exists') || msg?.includes('duplicate')) {
        onError('Cet email est déjà enregistré. L\'utilisateur a été rattaché au système.');
        onSuccess();
      } else {
        onError(msg || 'Erreur lors de la création.');
      }
    } else {
      onSuccess();
    }
  };

  return (
    <ModalWrapper title="Nouvel utilisateur" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom complet *</label>
          <input
            type="text"
            value={form?.fullName}
            onChange={(e) => setForm({ ...form, fullName: e?.target?.value })}
            placeholder="Prénom Nom"
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
          <input
            type="email"
            value={form?.email}
            onChange={(e) => setForm({ ...form, email: e?.target?.value })}
            placeholder="email@exemple.com"
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mot de passe *</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={form?.password}
              onChange={(e) => setForm({ ...form, password: e?.target?.value })}
              placeholder="Minimum 8 caractères"
              required
              minLength={8}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rôle *</label>
          <select
            value={form?.role}
            onChange={(e) => setForm({ ...form, role: e?.target?.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
          >
            {ROLE_OPTIONS?.map((r) => (
              <option key={r?.value} value={r?.value}>{r?.label}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
            Annuler
          </button>
          <button type="submit" disabled={loading} className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors">
            {loading ? 'Création...' : 'Créer'}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}

// ─── EditUserModal ────────────────────────────────────────────
function EditUserModal({ user, currentUser, onClose, onSuccess, onError }) {
  const [form, setForm] = useState({ fullName: user?.full_name || '', role: user?.role || 'admin' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    const { error } = await updateUserProfile(user?.id, { full_name: form?.fullName });
    if (!error && form?.role !== user?.role && user?.role !== 'super_admin') {
      await updateUserRole(user?.id, form?.role, currentUser?.id);
    }
    setLoading(false);
    if (error) {
      onError('Erreur lors de la mise à jour.');
    } else {
      onSuccess();
    }
  };

  return (
    <ModalWrapper title="Modifier l'utilisateur" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
          <input type="email" value={user?.email} disabled className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-gray-400 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom complet</label>
          <input
            type="text"
            value={form?.fullName}
            onChange={(e) => setForm({ ...form, fullName: e?.target?.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
          />
        </div>
        {user?.role !== 'super_admin' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rôle</label>
            <select
              value={form?.role}
              onChange={(e) => setForm({ ...form, role: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
            >
              {ROLE_OPTIONS?.map((r) => (
                <option key={r?.value} value={r?.value}>{r?.label}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
            Annuler
          </button>
          <button type="submit" disabled={loading} className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors">
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}

// ─── DeleteConfirmModal ───────────────────────────────────────
function DeleteConfirmModal({ user, onClose, onConfirm }) {
  return (
    <ModalWrapper title="Supprimer l'utilisateur" onClose={onClose}>
      <div className="text-center py-2">
        <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <Trash2 className="h-7 w-7 text-red-600 dark:text-red-400" />
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
          Voulez-vous vraiment supprimer l'utilisateur :
        </p>
        <p className="font-semibold text-gray-900 dark:text-white mb-1">{user?.full_name || user?.email}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{user?.email}</p>
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-left mb-6">
          <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            L'historique métier sera conservé. Les références seront remplacées par "Utilisateur supprimé".
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
            Annuler
          </button>
          <button onClick={onConfirm} className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors">
            Supprimer
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}

// ─── ModalWrapper ─────────────────────────────────────────────
function ModalWrapper({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
